import React, { useEffect, useState } from 'react';
import { useDrawers } from '../../contexts/DrawerContext';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { ArrowLeft, CheckCircle2, ChevronRight } from 'lucide-react';
type Step = 1 | 2 | 3 | 4;
const MATTER_TYPES = ['Buying', 'Selling', 'Contract review', 'Property transfer', 'SMSF or trust', 'Commercial property', 'Subdivision', 'Solicitor certificate', 'Other'];
const inputClass = 'w-full h-12 px-4 rounded-xl bg-surface border border-border text-ink focus:border-accent focus:ring-2 focus:ring-accent/25 outline-none transition';
const labelClass = 'block text-sm font-medium text-ink mb-1.5';
export function QuoteDrawer() {
  const {
    isQuoteOpen,
    closeQuote
  } = useDrawers();
  const [step, setStep] = useState<Step>(1);
  const [matterType, setMatterType] = useState('');
  useEffect(() => {
    if (!isQuoteOpen) {
      setTimeout(() => {
        setStep(1);
        setMatterType('');
      }, 300);
    }
  }, [isQuoteOpen]);
  const handleNext = () => setStep(s => Math.min(s + 1, 4) as Step);
  const handleBack = () => setStep(s => Math.max(s - 1, 1) as Step);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(4);
  };
  return <Drawer isOpen={isQuoteOpen} onClose={closeQuote} title="Get a free quote" subtitle={step < 4 ? `Step ${step} of 3` : undefined} data-id="element-28">
      {step < 4 && <div className="px-6 pt-5 pb-3 bg-surface border-b border-border flex gap-2" data-id="element-29">
          {[1, 2, 3].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${step >= i ? 'bg-accent' : 'bg-border'}`} data-id="element-30" />)}
        </div>}

      <div className="p-6" data-id="element-31">
        {step === 1 && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-32">
            <div data-id="element-33">
              <h3 className="text-lg font-serif text-ink mb-1" data-id="element-34">
                What do you need help with?
              </h3>
              <p className="text-sm text-muted-ink" data-id="element-35">
                Select the primary matter type.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2.5" data-id="element-36">
              {MATTER_TYPES.map(type => <button key={type} onClick={() => {
            setMatterType(type);
            handleNext();
          }} className={`flex items-center justify-between p-4 rounded-xl border text-left transition-all ${matterType === type ? 'border-accent bg-accent-soft text-accent-dark font-semibold' : 'border-border bg-surface hover:border-accent/40 hover:bg-accent-soft/40 text-ink'}`} data-id="element-37">
                  {type}
                  <ChevronRight className="w-4 h-4 opacity-50" data-id="element-38" />
                </button>)}
            </div>
          </div>}

        {step === 2 && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-39">
            <div className="flex items-center gap-3 mb-2" data-id="element-40">
              <button onClick={handleBack} className="p-2 -ml-2 rounded-full text-muted-ink hover:text-ink hover:bg-soft-panel transition-colors" aria-label="Back" data-id="element-41">
                <ArrowLeft className="w-5 h-5" data-id="element-42" />
              </button>
              <div data-id="element-43">
                <h3 className="text-lg font-serif text-ink" data-id="element-44">
                  Property information
                </h3>
                <p className="text-sm text-muted-ink" data-id="element-45">
                  Tell us about the transaction.
                </p>
              </div>
            </div>

            <div className="space-y-4" data-id="element-46">
              <div className="grid grid-cols-2 gap-4" data-id="element-47">
                <div data-id="element-48">
                  <label className={labelClass} data-id="element-49">Suburb</label>
                  <input type="text" className={inputClass} data-id="element-50" />
                </div>
                <div data-id="element-51">
                  <label className={labelClass} data-id="element-52">State</label>
                  <select className={`${inputClass} appearance-none`} data-id="element-53">
                    <option data-id="element-54">VIC</option>
                    <option data-id="element-55">NSW</option>
                    <option data-id="element-56">QLD</option>
                    <option data-id="element-57">Other</option>
                  </select>
                </div>
              </div>

              <div data-id="element-58">
                <label className={labelClass} data-id="element-59">Property type</label>
                <select className={`${inputClass} appearance-none`} data-id="element-60">
                  <option data-id="element-61">House</option>
                  <option data-id="element-62">Apartment or unit</option>
                  <option data-id="element-63">Townhouse</option>
                  <option data-id="element-64">Vacant land</option>
                  <option data-id="element-65">Commercial</option>
                </select>
              </div>

              <div data-id="element-66">
                <label className={labelClass} data-id="element-67">Sale method</label>
                <select className={`${inputClass} appearance-none`} data-id="element-68">
                  <option data-id="element-69">Private sale</option>
                  <option data-id="element-70">Auction</option>
                  <option data-id="element-71">Off-the-plan</option>
                  <option data-id="element-72">Not applicable</option>
                </select>
              </div>

              <div data-id="element-73">
                <label className={labelClass} data-id="element-74">Purchasing entity</label>
                <select className={`${inputClass} appearance-none`} data-id="element-75">
                  <option data-id="element-76">Individual(s)</option>
                  <option data-id="element-77">Company</option>
                  <option data-id="element-78">Trust</option>
                  <option data-id="element-79">SMSF</option>
                </select>
              </div>

              <div data-id="element-80">
                <label className={labelClass} data-id="element-81">
                  Auction or signing deadline (if applicable)
                </label>
                <input type="date" className={inputClass} data-id="element-82" />
              </div>

              <Button fullWidth variant="accent" onClick={handleNext} className="mt-2" data-id="element-83">
                Continue to contact details
              </Button>
            </div>
          </div>}

        {step === 3 && <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-84">
            <div className="flex items-center gap-3 mb-2" data-id="element-85">
              <button type="button" onClick={handleBack} className="p-2 -ml-2 rounded-full text-muted-ink hover:text-ink hover:bg-soft-panel transition-colors" aria-label="Back" data-id="element-86">
                <ArrowLeft className="w-5 h-5" data-id="element-87" />
              </button>
              <div data-id="element-88">
                <h3 className="text-lg font-serif text-ink" data-id="element-89">Contact details</h3>
                <p className="text-sm text-muted-ink" data-id="element-90">
                  Where should we send the quote?
                </p>
              </div>
            </div>

            <div className="space-y-4" data-id="element-91">
              <div data-id="element-92">
                <label className={labelClass} data-id="element-93">Full name</label>
                <input required type="text" autoComplete="name" className={inputClass} data-id="element-94" />
              </div>
              <div data-id="element-95">
                <label className={labelClass} data-id="element-96">Email</label>
                <input required type="email" autoComplete="email" className={inputClass} data-id="element-97" />
              </div>
              <div data-id="element-98">
                <label className={labelClass} data-id="element-99">Phone</label>
                <input required type="tel" autoComplete="tel" className={inputClass} data-id="element-100" />
              </div>
              <div data-id="element-101">
                <label className={labelClass} data-id="element-102">Preferred contact method</label>
                <select className={`${inputClass} appearance-none`} data-id="element-103">
                  <option data-id="element-104">Email</option>
                  <option data-id="element-105">Phone call</option>
                  <option data-id="element-106">Either is fine</option>
                </select>
              </div>
              <div data-id="element-107">
                <label className={labelClass} data-id="element-108">Message (optional)</label>
                <textarea rows={3} className="w-full p-4 rounded-xl bg-surface border border-border text-ink focus:border-accent focus:ring-2 focus:ring-accent/25 outline-none transition resize-none" data-id="element-109"></textarea>
              </div>

              <Button fullWidth variant="accent" type="submit" className="mt-2" data-id="element-110">
                Submit request
              </Button>

              <p className="text-xs text-muted-ink text-center" data-id="element-111">
                Submitting information does not by itself create a
                solicitor-client relationship.
              </p>
            </div>
          </form>}

        {step === 4 && <div className="py-12 flex flex-col items-center text-center space-y-6 animate-in zoom-in-95 duration-300" data-id="element-112">
            <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center text-accent" data-id="element-113">
              <CheckCircle2 className="w-8 h-8" data-id="element-114" />
            </div>
            <div className="space-y-2" data-id="element-115">
              <h3 className="text-2xl font-serif text-ink" data-id="element-116">Thank you.</h3>
              <p className="text-muted-ink leading-relaxed max-w-xs mx-auto" data-id="element-117">
                The team will review the information and contact you about the
                proposed next step.
              </p>
            </div>
            <Button onClick={closeQuote} variant="outline" className="mt-2" data-id="element-118">
              Close
            </Button>
          </div>}
      </div>
    </Drawer>;
}